import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  useTheme
} from "@mui/material";
import { useFormikContext } from "formik";
import { OnboardingFormValues } from "@/pages/onboarding/Onboarding";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, File02Icon, LinkSquare02Icon, MarketingIcon, Shield01Icon } from "@hugeicons/core-free-icons";
import { LEGAL_DOCUMENTS } from "@/constants/legal";
import { LegalDocument } from "@/types/legal";
import { useAppSelector } from "@/hooks";
import LegalCard from "./LegalCard";

// Drawer interno para ver el contenido completo
interface LegalPreviewDrawerProps {
  document: LegalDocument | null;
  onClose: () => void;
}

export const LegalPreviewDrawer: React.FC<LegalPreviewDrawerProps> = ({ document, onClose }) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={!!document}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 550 } }
      }}
    >
      {document && (
        <Stack height="100%" justifyContent="space-between">
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" sx={{ fontFamily: "Fredoka", fontWeight: 600 }}>
                {document.title}
              </Typography>
              <IconButton onClick={onClose}>
                <HugeiconsIcon icon={Cancel01Icon} size={20} />
              </IconButton>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Versión {document.version} • {new Date(document.publishedAt).toLocaleDateString()}
            </Typography>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              "& h1": { fontSize: "2rem", fontWeight: 700, mb: 2, mt: 0, fontFamily: theme.typography?.fontFamily, color: theme.palette.text.primary },
              "& h2": { fontSize: "1.5rem", fontWeight: 600, mt: 0, mb: 2, fontFamily: theme.typography?.fontFamily, color: theme.palette.text.primary },
              "& h3": { fontSize: "1.2rem", fontWeight: 600, mt: 3, mb: 1.5, fontFamily: theme.typography?.fontFamily, color: theme.palette.primary.main },
              "& p": { fontSize: "1rem", lineHeight: 1.8, color: theme.palette.text.secondary, mb: 2, textAlign: "justify", fontFamily: theme.typography?.fontFamily },
              "& ul, & ol": { pl: 3, mb: 2, fontSize: "1rem", lineHeight: 1.8, color: theme.palette.text.secondary, fontFamily: theme.typography?.fontFamily },
              "& li": { mb: 1, fontFamily: theme.typography?.fontFamily },
              "& strong": { fontWeight: 600, color: theme.palette.text.primary, fontFamily: theme.typography?.fontFamily },
              "& a": { color: theme.palette.primary.main, textDecoration: "underline", fontFamily: theme.typography?.fontFamily },
            }}
            dangerouslySetInnerHTML={{ __html: document.content }}
          />

          {/* Footer */}
          <Box sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
            <Button fullWidth variant="contained" onClick={onClose}>
              Cerrar Documento
            </Button>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
};

// Componente principal
interface LegalConsentDrawerProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const LegalConsentDrawer: React.FC<LegalConsentDrawerProps> = ({ open, onClose, onAccept }) => {
  const theme = useTheme();
  const { values, setFieldValue } = useFormikContext<OnboardingFormValues>();
  const [previewDoc, setPreviewDoc] = useState<LegalDocument | null>(null);
  const legalDocs = LEGAL_DOCUMENTS;
  const currentUser = useAppSelector((state) => state?.auth?.user);


  const handleAcceptAndContinue = () => {
    if (
      values.legal?.terms?.accepted &&
      values.legal?.privacy?.accepted
    ) {
      onAccept();
    }
  };
  const toogleLegalCheck = (key: "terms" | "privacy" | "marketing", checked: boolean) => {
    if (checked) {
      setFieldValue(`legal.${key}.accepted`, true);
      setFieldValue(`legal.${key}.acceptedAt`, Date.now());
      setFieldValue(`legal.${key}.acceptedBy`, currentUser?.uid || "");

      // CORRECCIÓN: Solo 'terms' y 'privacy' tienen versión en legalDocs
      if (key === "terms" || key === "privacy") {
        setFieldValue(`legal.${key}.version`, legalDocs[key].version);
      } else {
        // Para marketing, asignamos una versión por defecto o undefined
        setFieldValue(`legal.${key}.version`, "1.0.0");
      }
    } else {
      setFieldValue(`legal.${key}.accepted`, false);
      setFieldValue(`legal.${key}.acceptedAt`, undefined);
      setFieldValue(`legal.${key}.acceptedBy`, undefined);
      setFieldValue(`legal.${key}.version`, undefined);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500 },
            boxShadow: 24,
          },
        }}
      >
        <Stack height="100%" sx={{ p: 4 }}>
          {/* Header */}
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" sx={{ fontFamily: "Fredoka", fontWeight: 600 }}>
                Todo listo para empezar
              </Typography>
              <IconButton onClick={onClose}>
                <HugeiconsIcon icon={Cancel01Icon} size={20} />
              </IconButton>
            </Stack>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Para que disfrutes de la mejor experiencia, por favor echa un vistazo a nuestros acuerdos.
            </Typography>
          </Stack>

          {/* Cards de Documentos */}
          <Stack spacing={2} sx={{ my: 4, flex: 1 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: 1 }}>
              Documentos Importantes
            </Typography>

            <LegalCard
              checked={Boolean(values.legal?.terms?.accepted)}
              onChange={(checked) => toogleLegalCheck("terms", checked)}
              title="Términos y Condiciones"
              description="Conozco las reglas y lineamientos legales para el uso de la plataforma."
              icon={
                <HugeiconsIcon
                  icon={File02Icon}
                  color={theme.palette.primary.main}
                />
              }
              link={
                <IconButton onClick={() => setPreviewDoc(legalDocs.terms)} sx={{ ml: 1 }}>
                  <HugeiconsIcon icon={LinkSquare02Icon} color={theme.palette.primary.main} />
                </IconButton>
              }
              tag={`v${legalDocs.terms.version}`}
            />

            <LegalCard
              checked={Boolean(values.legal?.privacy?.accepted)}
              onChange={(checked) => toogleLegalCheck("privacy", checked)}
              title="Privacy Policy"
              description="Entiendo cómo son manejados mis datos personales y privacidad."
              icon={
                <HugeiconsIcon
                  icon={Shield01Icon}
                  color={theme.palette.primary.main}
                />
              }
              link={
                <IconButton onClick={() => setPreviewDoc(legalDocs.privacy)} sx={{ ml: 1 }}>
                  <HugeiconsIcon icon={LinkSquare02Icon} color={theme.palette.primary.main} />
                </IconButton>
              }
              tag={`v${legalDocs.privacy.version}`}
            />
            {/*  */}
            <LegalCard
              checked={Boolean(values.legal?.marketing?.accepted)}
              onChange={(checked) => toogleLegalCheck("marketing", checked)}
              title="Novedades"
              description="¡Me gustaría recibir novedades y consejos útiles!"
              icon={
                <HugeiconsIcon
                  icon={MarketingIcon}
                  color={theme.palette.primary.main}
                />
              }
            />

          </Stack>

          {/* Botón Final */}
          <Box sx={{ pt: 4 }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={!values.legal?.terms?.accepted || !values.legal?.privacy?.accepted}
              onClick={handleAcceptAndContinue}
              sx={{ fontWeight: 600, height: 54, fontFamily: "Fredoka" }}
            >
              Aceptar y Continuar
            </Button>
          </Box>
        </Stack>
      </Drawer>

      {/* Drawer de preview */}
      <LegalPreviewDrawer document={previewDoc} onClose={() => setPreviewDoc(null)} />
    </>
  );
};

export default LegalConsentDrawer;